import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { OptionDropdown } from '@df/module-product-configuration/model/simple/custom-options/option/option-dropdown';
import { OptionRadio } from '@df/module-product-configuration/model/simple/custom-options/option/option-radio';
import { TCustomOption } from '@df/module-product-configuration/model/simple/custom-options/product-configuration-configurable-product-data.service';
import { DfUiCarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { Nexus } from '@tomandco/nexus-api-types';
import { ProductViewComplexService } from '../../product-view-complex.service';
import { ProductViewSwatchComponent } from '../../swatch/product-view-swatch.component';
import { ProductViewCustomOptionsComponent } from '../product-view-custom-options.component';
declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Catalog.Type {
        export interface IProductDataCustomOptionSelection {
            image?: string;
            swatch?: string;
        }
    }
}

let uid = 0;

interface ICustomOptionComplex {
    option: OptionRadio | OptionDropdown;
    groups: ICustomOptionComplexGroup[];
}

interface ICustomOptionComplexGroup {
    title?: string;
    values: Nexus.Lib.Catalog.Type.IProductDataCustomOptionSelection[];
    id: number;
}

@Component({
    imports: [FormsModule, GridModule, DfUiCarouselModule, ProductViewSwatchComponent, OptionLabelPipe],
    selector: 'product-view-custom-options-complex',
    templateUrl: './product-view-custom-options-complex.html',
    styleUrl: './product-view-custom-options-complex.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductViewCustomOptionsComplexComponent extends ProductViewCustomOptionsComponent {
    protected complexOptions: ICustomOptionComplex[] = [];

    allowedOptions = input<string[]>([]);

    forbiddenOptions = input<string[]>([]);

    private productViewComplexService = inject(ProductViewComplexService);

    protected override setOptions(options?: TCustomOption[]): void {
        const oldOptions = this.options || this.productViewComplexService.customOptions;
        this.productViewComplexService.customOptions = undefined;
        super.setOptions(options);
        this.complexOptions = this.getCustomOptionsComplex(options);
        this.complexOptions.forEach(i => {
            const previous = oldOptions?.find(oldOption => oldOption.data.title === i.option.data.title) as OptionRadio | OptionDropdown;
            if (previous?.value) {
                const previousTitle = previous.values.find(i => i.value === previous.value)?.title;
                if (previousTitle) {
                    const matchingNew = i.option.values.find(i => i.title.toLowerCase() === previousTitle.toLowerCase());
                    if (matchingNew) {
                        i.option.select(matchingNew);
                        this.detectChanges();
                    }
                }
            }
            if (!i.option.value) {
                const firstOption = i.groups?.[0].values?.[0];
                if (firstOption) {
                    i.option.select(firstOption);
                    this.detectChanges();
                }
            }
        });
        this.detectChanges();
    }

    private getCustomOptionsComplex(options?: TCustomOption[]): ICustomOptionComplex[] {
        if (options?.length) {
            let allowedOptions = this.getAllowedOptions(options);

            return allowedOptions.map(allowedOption => {
                return {
                    option: allowedOption,
                    groups: this.getGroups([...allowedOption.values])
                };
            });
        }
        return [];
    }

    private getGroups(values: Nexus.Lib.Catalog.Type.IProductDataCustomOptionSelection[]): ICustomOptionComplexGroup[] {
        values = [...values].reverse();
        const groups: ICustomOptionComplexGroup[] = [];
        let lastHiddenIndex: number | undefined = undefined;
        values.forEach((value, index: number) => {
            const title = value.title;
            if (title.toLowerCase().endsWith('- hidden')) {
                groups.push({
                    title: value.title.slice(0, title.length - 8).trim(),
                    values: [...values.slice(lastHiddenIndex ? lastHiddenIndex + 1 : 0, index)].reverse(),
                    id: uid++
                });
                lastHiddenIndex = index;
            }
        });
        if (!lastHiddenIndex || lastHiddenIndex + 1 < values.length) {
            groups.push({
                values: [...values.splice(lastHiddenIndex ? lastHiddenIndex + 1 : 0)].reverse(),
                id: uid++
            });
        }
        return groups.reverse();
    }

    protected getSelectedLabel(option: OptionRadio | OptionDropdown): string | undefined {
        if (option) {
            const selection = option.value;
            if (selection) {
                const selectedOption = option.values.find(i => i.value === selection);
                return selectedOption?.title;
            }
        }
        return undefined;
    }

    private getAllowedOptions(options: TCustomOption[]): (OptionRadio | OptionDropdown)[] {
        let allowedOptions = options.filter(i => ['radio', 'dropdown'].indexOf(i.type) > -1) as (OptionRadio | OptionDropdown)[];
        function isAllowed(array: string[], title: string, including: boolean): boolean {
            if (array.length) {
                const index = array.map(i => i.toLowerCase()).indexOf(title);
                return including ? index > -1 : index === -1;
            }
            return true;
        }
        return allowedOptions.filter(i => {
            const title = i.data?.title?.toLowerCase();
            if (title) {
                return isAllowed(this.allowedOptions(), title, true) && isAllowed(this.forbiddenOptions(), title, false);
            }
            return true;
        });
    }

    protected select(option: OptionRadio | OptionDropdown, selection: Nexus.Lib.Catalog.Type.IProductDataCustomOptionSelection): void {
        option.select(selection);
        this.detectChanges();
    }

    protected get isSM(): boolean {
        return this.client.sizeId < 2;
    }

    get pcSlides(): number {
        return 3.15;
    }

    get pcSlidesCeil(): number {
        return Math.ceil(this.pcSlides);
    }

    protected get fabricStyle(): number | undefined {
        const fabricStyle = this.configuration.product$.value?.getAttr('fabric_style') as number | number[];
        return typeof fabricStyle === 'number' ? fabricStyle : undefined;
    }
}
